import React, {useEffect, useRef, useState} from "react";
import {Button, Flex, Input, InputGroup, Text} from "@chakra-ui/react";
import {CiCircleRemove} from "react-icons/ci";
import UploadIcon from "../../assets/upload.svg";
import {ALLOWED_TYPES, MAX_UPLOAD_FILE_SIZE_BYTES} from "../../models/constants/constants.ts";
import {getFileBaseName} from "../../utils/utils.ts";
import type {SanitizedPreset} from "../../utils/presetSanitizer.ts";
import type {SubtitleStep} from "./LanguagesStep.tsx";
import {OpenTemplateDropdown} from "./OpenTemplateDropdown.tsx";

const MAX_UPLOAD_FILES = 10;

interface DragAndDropProps {
  uploadFiles: File[];
  customConfigurationId?: string;
  selectedSourceLanguage: string;
  setUploadedFiles: (files: File[]) => void;
  fileNameToSeriesNameRelation: Record<string, string>;
  setFileNameToSeriesNameRelation: (relation: Record<string, string>) => void;
  setCustomConfiguration: (configId: string | undefined) => void;
  applyPreset: (preset: SanitizedPreset) => void;
  goToSubtitleStep: (step: SubtitleStep) => void;
  setFileDuration: (fileName: string, seconds: number) => void;
  renameFileDuration: (from: string, to: string) => void;
  removeFileDuration: (fileName: string) => void;
  handleErrors: (error: string, action?: "add" | "remove") => void;
}

const DragAndDrop: React.FC<DragAndDropProps> = (
  {
    uploadFiles,
    customConfigurationId,
    selectedSourceLanguage,
    setUploadedFiles,
    fileNameToSeriesNameRelation,
    setFileNameToSeriesNameRelation,
    setCustomConfiguration,
    applyPreset,
    goToSubtitleStep,
    setFileDuration,
    renameFileDuration,
    removeFileDuration,
    handleErrors
  }: DragAndDropProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileIndex, setSelectedFileIndex] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (uploadFiles.length && selectedFileIndex === null) {
      setSelectedFileIndex(0);
    }
  }, [uploadFiles.length, selectedFileIndex]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: FileList | null = event.target.files;
    if (files && validateNumOfFiles(files)) {
      const filesArray: File[] = Array.from(files).slice(0, MAX_UPLOAD_FILES - uploadFiles.length);
      batchValidateAndAddFiles(filesArray);
    }

    // ✅ Clear input so selecting the same file again will trigger `onChange`
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const files: FileList = event.dataTransfer.files;
    if (files && validateNumOfFiles(files)) {
      const filesArray: File[] = Array.from(files).slice(0, MAX_UPLOAD_FILES - uploadFiles.length);
      batchValidateAndAddFiles(filesArray);
    }
  };

  const validateNumOfFiles = (files: FileList): boolean => {
    if (uploadFiles.length + files.length > MAX_UPLOAD_FILES) {
      handleErrors("You can only upload up to 10 files.");
      setTimeout(() => handleErrors("You can only upload up to 10 files.", "remove"), 3000)
      return false;
    }
    return true;
  }

  const reportTransientError = (message: string) => {
    handleErrors(message, "add");
    setTimeout(() => handleErrors(message, "remove"), 3000);
  };

  const batchValidateAndAddFiles = (filesArray: File[]) => {
    const validatedFiles: File[] = [];
    const claimedFileNames: Set<string> = new Set(uploadFiles.map((f: File) => f.name));
    let processedCount: number = 0;

    const markProcessed = () => {
      processedCount++;
      if (processedCount !== filesArray.length || !validatedFiles.length) return;

      const updated: File[] = [...uploadFiles, ...validatedFiles];
      setUploadedFiles(updated);
      if (updated.length === validatedFiles.length) {
        setTimeout(() => setSelectedFileIndex(0), 0);
      }
    };

    filesArray.forEach((file: File) => {
      const fileType: string = file.type;

      if (!ALLOWED_TYPES[fileType]) {
        handleErrors("Only WMV, AVI, MP4, MKV, and MOV files are supported.");
        markProcessed();
        return;
      }
      handleErrors("Only WMV, AVI, MP4, MKV, and MOV files are supported.", "remove");

      if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
        reportTransientError(`${file.name} exceeds the 4GB per-file limit.`);
        markProcessed();
        return;
      }

      if (claimedFileNames.has(file.name)) {
        markProcessed();
        return;
      }
      claimedFileNames.add(file.name);

      const video: HTMLVideoElement = document.createElement("video");
      video.preload = "metadata";
      video.src = URL.createObjectURL(file);

      video.onloadedmetadata = () => {
        setFileDuration(file.name, video.duration);
        validatedFiles.push(file);
        markProcessed();
      };

      video.onerror = () => {
        reportTransientError(`${file.name} could not be read as a video.`);
        markProcessed();
      };
    });
  };


  const removeFile = (
    e: React.MouseEvent<SVGAElement, MouseEvent>,
    file: File,
    index: number,
  ) => {
    e.stopPropagation();
    if (fileNameToSeriesNameRelation[file.name]) {
      const updatedRelation = {...fileNameToSeriesNameRelation};
      delete updatedRelation[file.name];
      setFileNameToSeriesNameRelation(updatedRelation);
    }
    const updated: File[] = uploadFiles.filter((_: File, i: number) => i !== index);
    // Removing a file BEFORE the selected one shifts it left; only rebasing on an exact match left
    // the index dangling past the shortened array, and the rename input then read `undefined.name`.
    if (!updated.length) {
      setSelectedFileIndex(null);
    } else if (selectedFileIndex !== null && index < selectedFileIndex) {
      setSelectedFileIndex(selectedFileIndex - 1);
    } else if (selectedFileIndex === index) {
      setSelectedFileIndex(Math.min(index, updated.length - 1));
    }
    removeFileDuration(file.name);
    setUploadedFiles(updated);
  }

  return (
    <Flex className="w-full text-center justify-center flex-col items-center">
      {uploadFiles.length ? (
        <Flex className="w-full max-w-[40rem] flex-col gap-4">
          <Flex wrap="wrap" gap="2">
            {uploadFiles.map((file, index) => (
              <Flex
                key={index}
                align="center"
                px="3"
                py="1.5"
                bg={selectedFileIndex === index ? "brandPurple.500" : "n.500"}
                borderWidth="1px"
                borderColor={selectedFileIndex === index ? "brandPurple.500" : "n.500"}
                borderRadius="lg"
                cursor="pointer"
                onClick={() => setSelectedFileIndex(index)}
                transitionProperty="background-color, border-color"
                transitionDuration="200ms"
                _hover={{bg: selectedFileIndex === index ? "brandPurple.400" : "n.400"}}
                gap="2"
              >
                <Text color="white" fontSize="sm">
                  {getFileBaseName(file.name)}
                </Text>
                <CiCircleRemove
                  size={18}
                  className="cursor-pointer text-gray-300 hover:text-red-400"
                  onClick={(e: React.MouseEvent<SVGAElement, MouseEvent>) => removeFile(e, file, index)}
                />
              </Flex>
            ))}
          </Flex>

          {selectedFileIndex !== null && (
            <>
              <InputGroup
                className="w-full h-[52px] border border-transparent bg-transparent rounded-lg flex items-center group focus-within:border-brandPurple-500 focus-within:ring-2 focus-within:ring-brandPurple-500 relative"
              >
                {/* Left Icon */}
                <img src={UploadIcon} className="max-w-[22px] ml-2" alt="upload-file"/>

                {/* File Name Input */}
                <Input
                  autoFocus
                  type="text"
                  placeholder="Project Name"
                  value={getFileBaseName(uploadFiles[selectedFileIndex].name)}
                  onChange={(e) => {
                    const newName = e.target.value;
                    const ext: string | undefined = uploadFiles[selectedFileIndex].name.split(".").pop();

                    const nameExists: boolean = uploadFiles.some((f, i) =>
                      i !== selectedFileIndex && getFileBaseName(f.name) === newName
                    );
                    if (nameExists) return;

                    const updatedFile = new File(
                      [uploadFiles[selectedFileIndex]],
                      `${newName}.${ext}`,
                      {type: uploadFiles[selectedFileIndex].type}
                    );
                    renameFileDuration(uploadFiles[selectedFileIndex].name, updatedFile.name);

                    const updatedFileNameToSeriesRelation = {...fileNameToSeriesNameRelation};
                    if (fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name]) {
                      updatedFileNameToSeriesRelation[`${newName}.${ext}`] = fileNameToSeriesNameRelation[uploadFiles[selectedFileIndex].name];
                      delete updatedFileNameToSeriesRelation[uploadFiles[selectedFileIndex].name];
                      setFileNameToSeriesNameRelation(updatedFileNameToSeriesRelation);
                    }

                    setUploadedFiles(uploadFiles.map((f: File, i: number) => (i === selectedFileIndex ? updatedFile : f)));
                  }}
                  sx={{
                    boxShadow: "none !important",
                    borderColor: "transparent !important",
                    outline: "none !important",
                    _focus: {
                      boxShadow: "none !important",
                      borderColor: "transparent !important",
                      outline: "none !important",
                    },
                    _active: {
                      boxShadow: "none !important",
                      borderColor: "transparent !important",
                      outline: "none !important",
                    },
                    _hover: {
                      boxShadow: "none !important",
                      borderColor: "transparent !important",
                      outline: "none !important",
                    }
                  }}
                  className="w-full text-loginInputsText-500 bg-transparent border-none outline-none focus:ring-0 focus:border-transparent rounded-lg pl-12 py-2 peer"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  position="absolute"
                  right="2"
                  top="50%"
                  transform="translateY(-50%)"
                  bg="transparent"
                  zIndex={10}
                  _hover={{bg: "gray.700"}}
                  size="md"
                  p={1}
                >
                  <Text fontSize="3xl" color="white">+</Text>
                </Button>
              </InputGroup>
              <div className="flex flex-col items-start justify-center">
                <OpenTemplateDropdown
                  customConfigurationId={customConfigurationId}
                  selectedSourceLanguage={selectedSourceLanguage}
                  setCustomConfiguration={setCustomConfiguration}
                  applyPreset={applyPreset}
                  goToSubtitleStep={goToSubtitleStep}
                />
              </div>
            </>
          )}
        </Flex>
      ) : (
        <Flex
          className={`h-[25rem] w-[43.75rem] flex-col items-center justify-center gap-4 rounded-xl border p-6 cursor-pointer transition-[background-color,border-color] md:w-full ${
            dragging
              ? 'border-solid border-brandPurple-400 bg-brandPurple-500/10'
              : 'border-dashed border-brandPurple-500 bg-n-600'
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
        >
          <img
            src={UploadIcon}
            className={`max-w-[42px] transition-transform ${dragging ? 'scale-110' : 'scale-100'}`}
            alt="upload-file"
          />
          <Flex className="flex-col items-center justify-center gap-1">
            <Text className="text-lg font-semibold text-white text-balance">Drag your file to upload</Text>
            <Text className="text-xs text-n-400 text-pretty">Up to 10 videos, 4GB each</Text>
          </Flex>
          <Flex className="w-full max-w-[16rem] items-center justify-center gap-4">
            <div className="h-px flex-1 bg-n-500"></div>
            <Text className="text-xs uppercase tracking-wider text-n-400">OR</Text>
            <div className="h-px flex-1 bg-n-500"></div>
          </Flex>
          <Flex className="justify-center">
            <button
              type="button"
              className="rounded-lg border border-n-300 px-4 py-2 font-semibold text-white transition-[background-color,transform] hover:bg-n-100/10 active:scale-[0.96]"
            >
              Browse files
            </button>
          </Flex>
        </Flex>
      )}
      {/* Hidden File Input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileSelect}
        accept="video/*"
        multiple // <-- allow multiple files
      />
    </Flex>
  );
};

export default DragAndDrop;
